import { FunctionComponent, memo, useEffect } from "react";
import {
  FieldErrors,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../../Components/TextInput/TextInputFieldC";
import Select from "../../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { TutorCreate_Update } from "../../../../Redux/Slices/Booklet/Admin/Tutors/TutorsCreate_Update";
import { IRole } from "../../../../Pages/Role/IRole";
import { RolesRead } from "../../../../Redux/Slices/Booklet/Admin/Tutors/RoleRead ";

export interface ITutor {
  id?: number;
  username: string;
  email: string;
  phone_no: string;
  role: number;
  password?: string;
  confirm_password?: string;
  status: boolean;
}

interface TutorFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ITutor>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ITutor>>>;
  isEdit: boolean;
}

const TutorForm: FunctionComponent<TutorFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
  isEdit
}) => {
  const dispatch = useAppDispatch();

  const { data: listRoles } = useAppSelector((state) => state.RolesReadSlice);


const passwordRegex = /^(?=[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{6,}$/;

const tutorSchema = z
  .object({
    id: z.number().default(0),
    username: z
      .string({ required_error: "Please enter username" })
      .min(1, { message: "Username is required" })
      .trim(),
    email: z
      .string({ required_error: "Please enter email" })
      .email("Invalid email format")
      .trim(),
    phone_no: z
      .string({ required_error: "Please enter phone number" })
      .regex(/^\+?[0-9]{7,15}$/, "Invalid phone number"),
    role: z.number({ required_error: "Please select role" }),
    password: isEdit
      ? z.string().optional()
      : z
          .string()
          .min(6, { message: "Password must be at least 6 characters" })
          .regex(
            passwordRegex,
            "Password must start with a capital letter, include atlear one numeric and a special character"
          ),
    confirm_password: isEdit
      ? z.string().optional()
      : z.string().min(6, { message: "Confirm password is required" }),
    status: z.boolean({ required_error: "Please select status" }),
  })
  .refine(
    (data) => isEdit || data.password === data.confirm_password,
    {
      path: ["confirm_password"],
      message: "Passwords do not match",
    }
  );



  const defaultTutorRoleId = listRoles?.data.find(
    (role: IRole) => role.name === "TUTOR"
  )?.id;

 
  const methods = useForm<ITutor>({
    defaultValues: {
      id: initialValues?.id ?? 0,
      username: initialValues?.username ?? "",
      email: initialValues?.email ?? "",
      phone_no: initialValues?.phone_no ?? "",

      password: "",
      confirm_password: "",
      status: initialValues?.status ?? true,
    },
    resolver: zodResolver(tutorSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ITutor> = (values) => {
    const payload = {
      ...values,
      id: values.id ?? 0,
      role: defaultTutorRoleId,
    };
    if (!values.password) {
      delete payload.password;
      delete payload.confirm_password;
    }
    console.log("payload", payload);
    dispatch(TutorCreate_Update(payload));
  };

  // const onError = (error: FieldErrors<ITutor>) => {
  //   console.log("error", error);
  // };
  console.log(isEdit, "role options");

  useEffect(() => {
    dispatch(RolesRead());
  }, []);

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues, methods]);

  useEffect(() => {
    return () => {
      setInitialValues({});
    };
  }, [setInitialValues]);

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        {/* Username */}
        <TextInputFieldC name="username" label="Username" type="text" />

        {/* Email */}
        <TextInputFieldC name="email" label="Email" type="email" />

        {/* Phone */}
        <TextInputFieldC name="phone_no" label="Phone Number" type="text" />

        {/* Role */}
        {/* <Select
          name="role"
          label="Role"
          options={roleOptions}
          value={
            roleOptions.find(
              (option) => option.value === methods.getValues("role")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("role", val?.value, { shouldValidate: true });
          }}
        /> */}

        {!isEdit ===true && (
          <>
            <TextInputFieldC name="password" label="Password" type="password" />
            <TextInputFieldC
              name="confirm_password"
              label="Confirm Password"
              type="password"
            />
          </>
        )}

        {/* Status */}
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={
            statusOptions.find(
              (option) => option.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(TutorForm);
